// Maps sidebar link hrefs to the RBAC (action, subject) required to see them.
// ONLY hrefs listed here are permission-gated; any link without an entry passes
// through unchanged (so this is additive — it never hides menu items that
// aren't part of the RBAC subject catalog yet).
//
// Actions/subjects use the shared RbacActions/RbacSubjects constants; matching
// is done with the shared can() logic (a `Manage` grant covers every action on
// that same subject).

import {
  RbacActions,
  RbacSubjects,
  type RbacAction,
  type RbacSubject,
} from './constants'

export interface NavAbility {
  action: RbacAction
  subject: RbacSubject
}

/** Prefix for the bulk-termination nav entry and the CMv2 button. */
export const BULK_TERMINATIONS_NAV_HREF =
  '/v2-census-management/bulk-terminations'

/** Prefix for the termination-tracking nav entry. */
export const TERMINATION_TRACKING_NAV_HREF =
  '/v2-census-management/termination-tracking'

/** HRIS > API Visibility > Payroll API Activity menu entry. */
export const PAYROLL_API_ACTIVITY_NAV_HREF =
  '/hris/api-visibility/payroll-api-activity'

// Keyed by href prefix — a link matches if its href starts with the key, so
// '/funding-invoice/invoice-list' matches the '/funding-invoice' entry.
//
// Each entry is a LIST of abilities with OR semantics: the link shows if the
// user satisfies ANY of them (a `Manage` grant covers every action on that same
// subject). This lets a surface with several sub-pages show when the user can
// reach at least one.
//
// This map holds ONLY the FINER per-surface subjects. The baseline
// Access/AdminPortal floor is applied separately (in useNavigationLinks, mirror
// of the adminShellOnEnter route gate): a user without Access/AdminPortal sees
// no admin links at all, so it would be redundant to list every admin surface
// here. List a link here only when it needs a subject BEYOND the AdminPortal
// floor.
export const NAV_ABILITY_BY_HREF_PREFIX: ReadonlyArray<
  readonly [string, ReadonlyArray<NavAbility>]
> = [
  // The Finance surface (/finance) submenu covers Billing Profiles + Invoice
  // Management (+ the standalone Funding Invoice list). Show it if the user can
  // reach ANY of those sub-surfaces.
  [
    '/finance',
    [
      { action: RbacActions.Manage, subject: RbacSubjects.FundingInvoice },
      { action: RbacActions.Read, subject: RbacSubjects.BillingProfile },
      { action: RbacActions.Manage, subject: RbacSubjects.InvoiceManagement },
    ],
  ],
  [
    '/funding-invoice',
    [{ action: RbacActions.Manage, subject: RbacSubjects.FundingInvoice }],
  ],
  // Census surfaces expose US-restricted member data. `/v2-census-management`
  // also covers its bulk-terminations / termination-tracking sub-links.
  [
    '/census-management',
    [{ action: RbacActions.Manage, subject: RbacSubjects.UsRestrictedData }],
  ],
  [
    '/v2-census-management',
    [{ action: RbacActions.Manage, subject: RbacSubjects.UsRestrictedData }],
  ],
  [
    '/census-review',
    [{ action: RbacActions.Manage, subject: RbacSubjects.UsRestrictedData }],
  ],
  ['/settings', [{ action: RbacActions.Read, subject: RbacSubjects.Settings }]],
  [
    '/permission-management',
    [
      {
        action: RbacActions.Read,
        subject: RbacSubjects.PermissionManagement,
      },
    ],
  ],
  // More specific than `/v2-census-management`. Matching returns the first
  // prefix, so keep this entry before any future census-management prefix.
  [
    BULK_TERMINATIONS_NAV_HREF,
    [
      {
        action: RbacActions.Manage,
        subject: RbacSubjects.UsRestrictedData,
      },
    ],
  ],
  [
    TERMINATION_TRACKING_NAV_HREF,
    [
      {
        action: RbacActions.Manage,
        subject: RbacSubjects.UsRestrictedData,
      },
    ],
  ],
  [
    PAYROLL_API_ACTIVITY_NAV_HREF,
    [
      {
        action: RbacActions.Manage,
        subject: RbacSubjects.UsRestrictedData,
      },
    ],
  ],
  [
    '/tools',
    [{ action: RbacActions.Manage, subject: RbacSubjects.UsRestrictedData }],
  ],
]

/**
 * Returns the abilities a link href requires (OR semantics — satisfying any one
 * shows the link), or undefined when the link is not RBAC-gated (and should
 * always be shown, subject to existing role/flag rules).
 */
export function navAbilitiesForHref(
  href: string
): ReadonlyArray<NavAbility> | undefined {
  const match = NAV_ABILITY_BY_HREF_PREFIX.find(([prefix]) =>
    href.startsWith(prefix)
  )
  return match?.[1]
}
